import {NextRouter, useRouter} from 'next/router';
import {useCallback, useMemo, useState} from 'react';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {StaticLandingRankingThemeMoreFetcher} from '../../../../common/fetchers/StaticLandingRankingThemeMoreFetcher';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {LogTabType} from '../../../../common/LogTabType.enum';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {requestContext} from '../../../../common/requestContext';
import {Icon} from '../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {BelowDefaultTab} from '../../../../design-system/organisms/BelowDefaultTab/BelowDefaultTab';
import {Dialog} from '../../../../design-system/organisms/Dialog';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {SubTab} from '../../../../design-system/organisms/SubTab/SubTab';
import {SubtabItemActiveColorScheme} from '../../../../design-system/organisms/SubTabItem/constants';
import type {SubTabItemType} from '../../../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import {
  StaticLandingRankingThemeSection,
  StaticLandingRankingThemeSectionParam,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {showRankingHelpDialog} from '../../../../main/showRankingHelpDialog';
import {CommonSectionProps} from '../../type';

export function StaticLandingRankingTheme({
  section,
  children,
}: CommonSectionProps) {
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const router = useRouter();

  const rankingThemeSection = section as Omit<
    StaticLandingRankingThemeSection,
    'groups'
  >;
  const {
    rankingTime,
    filterOptList,
    rankSortOptList,
    selectedFilterOpt,
    selectedRankSortOpt,
    promotionBanner,
    totalCount,
    param,
  } = rankingThemeSection;

  const currentFilterType = param.filterType ?? selectedFilterOpt?.param;
  const currentSortType = param.sortType ?? selectedRankSortOpt?.param;

  const currentFilterName = useMemo(
    () => filterOptList.find(opt => opt.param === currentFilterType)?.name,
    [filterOptList, currentFilterType]
  );
  const currentSortName = useMemo(
    () => rankSortOptList.find(opt => opt.param === currentSortType)?.name,
    [rankSortOptList, currentSortType]
  );

  const [isShowSortDialog, setIsShowSortDialog] = useState(false);

  const onClickFilterItem = useCallback(
    (filterType: string) => {
      if (filterType !== currentFilterType) {
        routeWithParam({router, param, filterType});
      }
    },
    [currentFilterType, param, router]
  );

  const onClickSortItem = useCallback(
    (sortType: string) => {
      setIsShowSortDialog(false);
      if (sortType !== currentSortType) {
        routeWithParam({router, param, sortType});
      }
    },
    [currentSortType, param, router]
  );

  // SubTab 의 href 는 onClickTab 에서 router.push 로 처리하므로 생략한다.
  const filterTabItems: SubTabItemType[] = useMemo(
    () =>
      filterOptList.map(opt => ({
        name: opt.name,
        param: opt.param,
      })),
    [filterOptList]
  );

  const hasSort = rankSortOptList.length > 0;

  return (
    <div
      className="flex w-full grow flex-col"
      {...putTiaraObjectProps({
        customProps: {
          page_filter: currentFilterName,
          page_sort: currentSortName,
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...{
        [PageViewCustomPropsDataKey.TabType]: 'tab_type=landing_single',
      }}
    >
      {filterTabItems.length > 0 && currentFilterType && (
        <TabPositionSticky
          depth={3}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <SubTab
            tabItems={filterTabItems}
            currentTabItemParam={currentFilterType}
            activeColorScheme={SubtabItemActiveColorScheme['2nd']}
            onClickTab={(clickedTabItemId, e) => {
              aggregateAndSendLog({
                targetElem: e.currentTarget as HTMLElement,
                actionName: '필터_클릭',
                actionType: 'Event',
                click: {
                  copy: filterOptList.find(
                    opt => opt.param === clickedTabItemId
                  )?.name,
                },
                customProps: {
                  user_action: 'click',
                  page_filter: currentFilterName,
                  page_sort: currentSortName,
                },
              });
              onClickFilterItem(clickedTabItemId);
            }}
            logTabType={LogTabType.Genre}
            {...putTiaraObjectProps({
              click: {
                layer1: '필터정렬',
              },
            })}
          />
        </TabPositionSticky>
      )}

      {(rankingTime || (hasSort && currentSortName)) && (
        <TabPositionSticky
          depth={4}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <BelowDefaultTab className="px-15pxr">
            {rankingTime && (
              <BelowDefaultTab.TextLabel>
                <button
                  type="button"
                  onClick={() => showRankingHelpDialog({isMobile})}
                  className="flex cursor-pointer items-center space-x-4pxr"
                >
                  <span>{rankingTime}</span>
                  <Icon type={IconType.ButtonHelpS} />
                </button>
              </BelowDefaultTab.TextLabel>
            )}
            {hasSort && currentSortName && (
              <BelowDefaultTab.SelectBox
                onClick={e =>
                  LOG_CLICK_EVENT('정렬선택_클릭', e, () =>
                    setIsShowSortDialog(true)
                  )
                }
                iconType={IconType.TabSorting}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '필터정렬',
                  },
                  customProps: {
                    page_filter: currentFilterName,
                    page_sort: currentSortName,
                  },
                })}
                {...{
                  [PageViewCustomPropsDataKey.PageSort]: `page_sort=${currentSortName}`,
                }}
              >
                {currentSortName}
              </BelowDefaultTab.SelectBox>
            )}
          </BelowDefaultTab>
        </TabPositionSticky>
      )}

      {promotionBanner && (
        <PromotionBannerContainer promotionBanner={promotionBanner} />
      )}

      {hasSort && (
        <Dialog
          isOpen={isShowSortDialog}
          onClose={() => setIsShowSortDialog(false)}
          isMobile={isMobile}
          {...putTiaraObjectProps({
            customProps: {
              page_filter: currentFilterName,
              page_sort: currentSortName,
            },
          })}
        >
          <Dialog.Dimmed />
          <Dialog.List className="space-y-0">
            {rankSortOptList.map(sortOpt => (
              <Dialog.Option
                key={sortOpt.param}
                onClick={e =>
                  LOG_CLICK_EVENT('정렬_클릭', e, () =>
                    onClickSortItem(sortOpt.param)
                  )
                }
                isChecked={sortOpt.param === currentSortType}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '필터정렬',
                    copy: sortOpt.name,
                  },
                })}
              >
                {sortOpt.name}
              </Dialog.Option>
            ))}
          </Dialog.List>
          <Dialog.BottomCombination>
            <Dialog.Button onClick={() => setIsShowSortDialog(false)}>
              {t('common_cancel_button')}
            </Dialog.Button>
          </Dialog.BottomCombination>
        </Dialog>
      )}

      {isGreaterThanZero(totalCount) && (
        <StaticLandingRankingThemeMoreFetcher section={rankingThemeSection}>
          <div className="flex w-full grow flex-col">{children}</div>
        </StaticLandingRankingThemeMoreFetcher>
      )}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>
          {t('landing_ranking_theme_series_empty')}
        </NoItem>
      )}
    </div>
  );
}

function routeWithParam({
  router,
  param,
  filterType,
  sortType,
}: {
  router: NextRouter;
  param: StaticLandingRankingThemeSectionParam;
  filterType?: string;
  sortType?: string;
}) {
  const nextFilter = filterType ?? param.filterType;
  const nextSort = sortType ?? param.sortType;
  const pathname = window.location.pathname;
  const query = {
    ...(nextFilter && {ranking_filter_type: nextFilter}),
    ...(nextSort && {sort_type: nextSort}),
  };

  router.push(
    {
      pathname,
      query,
    },
    undefined,
    {scroll: false}
  );
}
